// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Wordloom — three modes that PLAY differently (principle #14), not three dials. The
// spread is the RULE, not a number:
//   Weave  — 5×5, letters arrive BLIND one at a time, rows + columns score.
//   Sluice — 5×5, you see a QUEUE of the next 3 and may place ANY of them (which
//            letter AND which cell), so it is planning rather than coping with luck.
//   Deluge — 6×6, blind, rows + columns AND both main diagonals score, so the centre
//            cells are triple-weighted and the calculus genuinely changes.
//
// The host's mode travels FROZEN inside the round start (roundOpts), and a mode that
// changes board size means peers would otherwise play different grids on one seed.

export type LineSet = 'rc' | 'rcd'; // rows+cols, or +both diagonals

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  /** Square board side (rows === cols). */
  size: number;
  /** Which lines score. */
  lines: LineSet;
  /** How many upcoming letters are visible & choosable (1 = blind). */
  queueSize: number;
  /** Fraction of the letter bag that is a vowel (AEIOU), guaranteed by construction. */
  vowelRatio: number;
  /** Versus round time limit (ms). Solo is untimed. */
  roundMs: number;
}

export const MODES: Record<string, Mode> = {
  weave: {
    id: 'weave',
    name: 'Weave',
    blurb: 'A 5×5 grid, one letter at a time. Every row and every column scores its best word — weave carefully.',
    size: 5,
    lines: 'rc',
    queueSize: 1,
    vowelRatio: 0.44,
    roundMs: 135_000,
  },
  sluice: {
    id: 'sluice',
    name: 'Sluice',
    blurb: 'See the next THREE letters and place any of them, in any order. Planning beats luck.',
    size: 5,
    lines: 'rc',
    queueSize: 3,
    vowelRatio: 0.44,
    roundMs: 150_000,
  },
  deluge: {
    id: 'deluge',
    name: 'Deluge',
    blurb: 'A bigger 6×6 board where BOTH diagonals score too — the middle squares count three times over.',
    size: 6,
    lines: 'rcd',
    queueSize: 1,
    vowelRatio: 0.44,
    roundMs: 195_000,
  },
};

export const MODE_IDS = ['weave', 'sluice', 'deluge'] as const;
export const DEFAULT_MODE_ID = 'weave';

/** Validate an id off the wire and fall back rather than hand `undefined` on. */
export function modeOf(id: string | null | undefined): Mode {
  if (id && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE_ID];
}

export function cellCount(mode: Mode): number {
  return mode.size * mode.size;
}
